import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys';
import { casesService } from '../services/cases.service';
import type { CasesResponse, CaseFilters, Case } from '@lnr-v2/types';
import {
  createOptimisticItem,
  onError,
  onSettled,
} from '../lib/optimisticHelpers';

// Re-export types for backwards compatibility
export type { CasesResponse, CaseFilters };

interface UseCasesOptions {
  initialFilters?: CaseFilters;
  autoFetch?: boolean;
}

interface UseCasesReturn {
  data: CasesResponse | null;
  loading: boolean;
  error: Error | null;
  filters: CaseFilters;
  setFilters: (filters: CaseFilters) => void;
  updateFilter: <K extends keyof CaseFilters>(
    key: K,
    value: CaseFilters[K]
  ) => void;
  refetch: () => Promise<void>;
  createCase: (data: Partial<Case>) => Promise<void>;
  updateCase: (id: string, data: Partial<Case>) => Promise<void>;
  deleteCase: (id: string) => Promise<void>;
}

/**
 * Custom hook for managing cases/projects with React Query
 *
 * Benefits over previous implementation:
 * - Automatic request deduplication based on filters
 * - Intelligent caching (5 min stale time, 10 min cache time)
 * - Automatic background refetching
 * - Better error handling and retry logic
 * - Filter-based cache keys for pagination
 * - DevTools for debugging queries
 *
 * React Query automatically handles:
 * - Preventing duplicate concurrent requests with same filters
 * - Caching responses based on filter parameters
 * - Background refetching when data becomes stale
 * - Request cancellation
 * - Error retry with exponential backoff
 *
 * @param options - Hook options with initialFilters and autoFetch
 */

const sortedBy = 'displayOrder';
const sortedOrder = 'ASC';

export const useCases = ({
  initialFilters = {
    page: 1,
    limit: 20,
    sortBy: sortedBy,
    sortOrder: sortedOrder,
  },
  autoFetch = true,
}: UseCasesOptions = {}): UseCasesReturn => {
  const queryClient = useQueryClient();

  // Local state for filters (controlled by component)
  const [filters, setFilters] = useState<CaseFilters>(initialFilters);

  // Main query for fetching cases
  // React Query will cache results based on filter parameters
  const {
    data = null,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.cases.list(filters),
    queryFn: () => casesService.getAll(filters),
    // Only fetch if autoFetch is true
    enabled: autoFetch,
    // Additional options (defaults from queryClient are already optimal)
    staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    // Keep previous data while fetching new page (better UX for pagination)
    placeholderData: (previousData) => previousData,
  });

  // Convert query error to Error type for backwards compatibility
  const error = queryError ? (queryError as Error) : null;

  // Case mutations with optimistic updates
  // Cases are paginated like clients
  const createCaseMutation = useMutation({
    mutationFn: casesService.create,
    onMutate: async (newCase) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.cases.list(filters),
      });

      const previousData = queryClient.getQueryData<CasesResponse>(
        queryKeys.cases.list(filters)
      );

      // Create optimistic case with default values
      const optimisticCase = createOptimisticItem<Case>({
        ...newCase,
        name: newCase.name || newCase.title || '',
        clientName: newCase.clientName || 'Unknown Client',
        description: newCase.description || '',
        tags: newCase.tags || [],
        displayOrder: newCase.displayOrder || 0,
        isFeatured: newCase.isFeatured ?? false,
        status: newCase.status || 'DRAFT',
      });

      // Add optimistic case to the current page
      queryClient.setQueryData<CasesResponse>(
        queryKeys.cases.list(filters),
        (old) => {
          if (!old || !old.data) return old;
          return {
            ...old,
            data: {
              ...old.data,
              items: [optimisticCase, ...old.data.items],
              pagination: {
                ...old.data.pagination,
                total: old.data.pagination.total + 1,
              },
            },
          };
        }
      );

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      onError(queryClient, queryKeys.cases.list(filters), context);
    },
    onSuccess: (data) => {
      // Replace optimistic case with real data
      queryClient.setQueryData<CasesResponse>(
        queryKeys.cases.list(filters),
        (old) => {
          if (!old || !old.data) return old;
          return {
            ...old,
            data: {
              ...old.data,
              items: old.data.items.map((caseItem) =>
                caseItem.id.startsWith('temp-') ? data : caseItem
              ),
            },
          };
        }
      );
    },
    onSettled: () => {
      onSettled(queryClient, queryKeys.cases.all);
    },
  });

  const updateCaseMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Case> }) =>
      casesService.update(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.cases.list(filters),
      });

      const previousData = queryClient.getQueryData<CasesResponse>(
        queryKeys.cases.list(filters)
      );

      // Update the case in the current page
      queryClient.setQueryData<CasesResponse>(
        queryKeys.cases.list(filters),
        (old) => {
          if (!old || !old.data) return old;
          return {
            ...old,
            data: {
              ...old.data,
              items: old.data.items.map((caseItem) =>
                caseItem.id === id
                  ? {
                      ...caseItem,
                      ...data,
                      updatedAt: new Date().toISOString(),
                    }
                  : caseItem
              ),
            },
          };
        }
      );

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      onError(queryClient, queryKeys.cases.list(filters), context);
    },
    onSettled: () => {
      onSettled(queryClient, queryKeys.cases.all);
    },
  });

  const deleteCaseMutation = useMutation({
    mutationFn: casesService.delete,
    onMutate: async (id) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.cases.list(filters),
      });

      const previousData = queryClient.getQueryData<CasesResponse>(
        queryKeys.cases.list(filters)
      );

      // Remove the case from the current page
      queryClient.setQueryData<CasesResponse>(
        queryKeys.cases.list(filters),
        (old) => {
          if (!old || !old.data) return old;
          return {
            ...old,
            data: {
              ...old.data,
              items: old.data.items.filter((caseItem) => caseItem.id !== id),
              pagination: {
                ...old.data.pagination,
                total: old.data.pagination.total - 1,
              },
            },
          };
        }
      );

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      onError(queryClient, queryKeys.cases.list(filters), context);
    },
    onSettled: () => {
      onSettled(queryClient, queryKeys.cases.all);
    },
  });

  // Update filter with automatic page reset
  const updateFilter = useCallback(
    <K extends keyof CaseFilters>(key: K, value: CaseFilters[K]) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
        // Reset to page 1 when changing filters (except when changing page itself)
        ...(key !== 'page' ? { page: 1 } : {}),
      }));
    },
    []
  );

  // Wrapper functions to maintain backwards compatibility
  const createCase = async (data: Partial<Case>) => {
    await createCaseMutation.mutateAsync(data);
  };

  const updateCase = async (id: string, data: Partial<Case>) => {
    await updateCaseMutation.mutateAsync({ id, data });
  };

  const deleteCase = async (id: string) => {
    await deleteCaseMutation.mutateAsync(id);
  };

  // Wrapper for refetch to match old signature
  const refetchWrapper = async () => {
    await refetch();
  };

  return {
    data,
    loading,
    error,
    filters,
    setFilters,
    updateFilter,
    refetch: refetchWrapper,
    createCase,
    updateCase,
    deleteCase,
  };
};
